import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Stack, TextField, Typography } from '@mui/material';
import { ArrowBack, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PageContainer, SurfaceCard } from '../ui/ConvivialComponents';
import CityFilter from '../ui/CityFilter';
import TableListCard from '../ui/TableListCard';
import { getTableCity } from '../../utils/tablePresentation';
import { calendarDateKey, monthDays, monthStart } from '../../utils/tableCalendar';

const weekdayLabels = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
const weekdayNames = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];
const dayLabel = (date) => date.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

export default function IndexCalendar({ pins }) {
    const [today, setToday] = useState(() => new Date());
    const [month, setMonth] = useState(() => monthStart(new Date()));
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedCity, setSelectedCity] = useState('all');
    const todayKey = calendarDateKey(today);
    const currentMonth = monthStart(today);
    const visibleMonth = month < currentMonth ? currentMonth : month;
    const selectedDay = selectedDate && selectedDate >= todayKey ? selectedDate : null;

    // Refresh the lower date limit if the app stays open across midnight.
    useEffect(() => {
        const nextMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        const timer = setTimeout(() => setToday(new Date()), Math.max(1000, nextMidnight.getTime() - Date.now()));
        return () => clearTimeout(timer);
    }, [today]);

    const tables = useMemo(() => (Array.isArray(pins) ? pins : [])
        .filter((pin) => pin.type === 'TABLE' && calendarDateKey(pin.startAt) >= todayKey)
        .sort((first, second) => new Date(first.startAt) - new Date(second.startAt)), [pins, todayKey]);
    const cities = useMemo(() => [...new Set(tables.map(getTableCity).filter(Boolean))]
        .sort((first, second) => first.localeCompare(second, 'it')), [tables]);

    useEffect(() => {
        if (selectedCity !== 'all' && !cities.includes(selectedCity)) setSelectedCity('all');
    }, [cities, selectedCity]);

    const tablesByDay = useMemo(() => {
        const grouped = new Map();
        tables.forEach((table) => {
            if (selectedCity !== 'all' && getTableCity(table) !== selectedCity) return;
            const key = calendarDateKey(table.startAt);
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key).push(table);
        });
        return grouped;
    }, [tables, selectedCity]);
    const dayTables = tablesByDay.get(selectedDay) || [];
    const selectedDayDate = selectedDay ? new Date(`${selectedDay}T12:00:00`) : null;

    const changeDay = (value) => {
        // Native date pickers can still receive manually typed invalid/past dates.
        const date = new Date(`${value}T12:00:00`);
        if (!value || calendarDateKey(date) !== value || value < todayKey) return;
        setSelectedDate(value);
        setMonth(monthStart(date));
    };

    return (
        <PageContainer maxWidth="760px" sx={{ minWidth: 0, pt: selectedDay ? 3 : 2 }}>
            <Stack spacing={selectedDay ? 2.5 : { xs: 1.5, sm: 2 }} sx={{ minWidth: 0 }}>
                {selectedDay ? (
                    <Box>
                        <Button startIcon={<ArrowBack />} onClick={() => setSelectedDate(null)} sx={{ mb: 2, px: 0 }}>
                            Torna al calendario
                        </Button>
                        <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 27, sm: 36 }, lineHeight: 1.25, textTransform: 'capitalize', overflowWrap: 'anywhere' }}>
                            {dayLabel(selectedDayDate)}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 1 }} aria-live="polite">
                            {selectedCity === 'all' ? 'Tutte le città' : selectedCity} · {dayTables.length} {dayTables.length === 1 ? 'tavolo in programma' : 'tavoli in programma'}
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="h1" sx={{ color: 'primary.main', fontSize: { xs: 28, sm: 40 }, lineHeight: 1.25 }}>
                            Tavoli in calendario
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>Scegli quando incontrarti.</Typography>
                    </Box>
                )}

                <CityFilter cities={cities} selectedCity={selectedCity} onChange={setSelectedCity} />

                {selectedDay ? (
                    <>
                        <TextField
                            label="Giorno"
                            type="date"
                            fullWidth
                            value={selectedDay}
                            onChange={(event) => changeDay(event.target.value)}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: todayKey } }}
                            sx={{ '& input': { minWidth: 0 } }}
                        />
                        {dayTables.length === 0 && (
                            <SurfaceCard>
                                <Typography variant="h3" sx={{ color: 'primary.main', mb: 1 }}>Nessun tavolo in questa data</Typography>
                                <Typography sx={{ color: 'text.secondary' }}>Prova un altro giorno oppure cambia città.</Typography>
                            </SurfaceCard>
                        )}
                        {dayTables.map((table) => <TableListCard key={table.id} table={table} />)}
                    </>
                ) : (
                    <>
                        <SurfaceCard sx={{ p: { xs: 0, sm: 0 }, overflow: 'hidden', borderRadius: '16px' }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: { xs: 1, sm: 1.5 }, py: 0.5, gap: 0.5 }}>
                                <Typography id="calendar-month" variant="h3" aria-live="polite" sx={{ color: 'primary.main', fontSize: { xs: 16, sm: 20 }, lineHeight: 1.25, letterSpacing: '-0.4px', whiteSpace: 'nowrap', textTransform: 'capitalize', minWidth: 0 }}>
                                    {visibleMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                                </Typography>
                                <Stack direction="row" sx={{ flexShrink: 0 }}>
                                    <IconButton aria-label="Mese precedente" disabled={visibleMonth <= currentMonth} onClick={() => setMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1))} sx={{ width: 44, height: 44 }}>
                                        <ChevronLeft />
                                    </IconButton>
                                    <IconButton aria-label="Mese successivo" onClick={() => setMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1))} sx={{ width: 44, height: 44, color: 'primary.main' }}>
                                        <ChevronRight />
                                    </IconButton>
                                </Stack>
                            </Stack>
                            <Box aria-labelledby="calendar-month" sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                                {weekdayLabels.map((label, index) => (
                                    <Typography key={weekdayNames[index]} component="abbr" title={weekdayNames[index]} sx={{ textAlign: 'center', py: 0.75, fontSize: 11, fontWeight: 700, textDecoration: 'none', color: 'text.secondary' }}>{label}</Typography>
                                ))}
                                {monthDays(visibleMonth).map((date, index) => {
                                    const key = date ? calendarDateKey(date) : '';
                                    const past = key < todayKey;
                                    const count = tablesByDay.get(key)?.length || 0;
                                    return (
                                        <ButtonBase
                                            key={key || `empty-${index}`}
                                            disabled={!date || past}
                                            onClick={() => setSelectedDate(key)}
                                            aria-label={date ? `${dayLabel(date)}, ${count} ${count === 1 ? 'tavolo' : 'tavoli'}` : undefined}
                                            aria-hidden={!date || undefined}
                                            aria-current={key === todayKey ? 'date' : undefined}
                                            sx={{
                                                minWidth: 0,
                                                minHeight: { xs: 54, sm: 68 },
                                                flexDirection: 'column',
                                                justifyContent: 'flex-start',
                                                gap: 0.25,
                                                py: 0.5,
                                                borderTop: '1px solid',
                                                borderRight: index % 7 === 6 ? 0 : '1px solid',
                                                borderColor: 'background.surfaceContainer',
                                                bgcolor: !date || past ? 'background.surface' : 'background.paper',
                                                color: past ? 'text.disabled' : 'text.primary',
                                                '&:hover': { bgcolor: 'primary.light' },
                                                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.container', outlineOffset: -2 },
                                            }}
                                        >
                                            {date && <>
                                                <Box component="span" sx={{ width: 22, height: 22, display: 'grid', placeItems: 'center', borderRadius: '50%', fontSize: 12, border: key === todayKey ? '1px solid #8a0030' : '1px solid transparent', color: key === todayKey ? 'primary.container' : 'inherit' }}>{date.getDate()}</Box>
                                                {count > 0 && <Box component="span" sx={{ color: 'primary.container', fontSize: { xs: 10, sm: 12 }, lineHeight: 1.1, textAlign: 'center' }}>
                                                    <Box component="span" sx={{ display: 'inline-block', width: 4, height: 4, borderRadius: '50%', bgcolor: 'currentColor', mr: 0.4, verticalAlign: 'middle' }} />
                                                    <Box component="strong">{count}</Box>
                                                    <Box component="span" sx={{ display: 'block', fontSize: { xs: 9, sm: 11 } }}>{count === 1 ? 'tavolo' : 'tavoli'}</Box>
                                                </Box>}
                                            </>}
                                        </ButtonBase>
                                    );
                                })}
                            </Box>
                        </SurfaceCard>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Tocca un giorno per vedere i tavoli disponibili.</Typography>
                    </>
                )}
            </Stack>
        </PageContainer>
    );
}
